import React, { useState, useRef } from 'react';
import { Button, Typography, message, Modal, Select, Alert, Space, Tooltip } from 'antd';
import { UploadOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { fetchSqlDataWithQuery, importCsvExcel, importCsvExcelFromServer } from '../../services/api';
import SelectDCCFileModal from '../modals/SelectDCCFileModal';

const { Title } = Typography;

interface CsvExcelImportProps {
  tableName: string; // Required: the table to import into
  onSuccess?: (response: any) => void;
  onError?: (error: any) => void;
}

const CsvExcelImport: React.FC<CsvExcelImportProps> = ({ tableName, onSuccess, onError }) => {
  const [file, setFile] = useState<File | null>(null);
  const [dccSelected, setDccSelected] = useState<{ fullPath: string; name: string } | null>(null);
  const [dccSelectedMany, setDccSelectedMany] = useState<Array<{ fullPath: string; name: string }>>([]);
  const [dccImportStatus, setDccImportStatus] = useState<Record<string, { status: 'pending' | 'running' | 'success' | 'error'; error?: string }>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [showIfExistsModal, setShowIfExistsModal] = useState(false);
  const [ifExists, setIfExists] = useState<'append' | 'replace' | 'fail'>('append');
  const [isDccPickerOpen, setIsDccPickerOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = async () => {
    const safe = String(tableName || '').trim();
    if (!safe) return;
    if (!/^[a-zA-Z0-9_]+$/.test(safe)) {
      message.error('Invalid table name');
      return;
    }

    setIsDownloadingTemplate(true);
    try {
      const query = `
        SELECT
          column_name,
          data_type,
          is_nullable,
          is_generated,
          column_default,
          ordinal_position
        FROM information_schema.columns
        WHERE table_schema = 'public'
          AND table_name = '${safe}'
        ORDER BY ordinal_position;
      `;

      const resp: any = await fetchSqlDataWithQuery(query);
      const rows = Array.isArray(resp) ? resp : (resp?.data ?? []);
      const excluded = new Set(['id', 'created_at', 'updated_at', 'created_by', 'updated_by']);
      const cols = (rows || [])
        .map((r: any) => ({
          column_name: String(r?.column_name || ''),
          column_default: r?.column_default,
          is_generated: String(r?.is_generated || 'NEVER'),
        }))
        .filter((r: any) => r.column_name)
        .filter((r: any) => !excluded.has(r.column_name))
        // Exclude generated columns (e.g., generated always as (...) stored)
        .filter((r: any) => String(r?.is_generated || 'NEVER') === 'NEVER')
        // Exclude serial/identity-like columns (best-effort)
        .filter((r: any) => !String(r?.column_default || '').toLowerCase().includes('nextval('));

      if (!cols.length) {
        message.warning('No columns found for template (after exclusions)');
        return;
      }

      const headers = cols.map((c: any) => c.column_name);
      const emptyRow: any = {};
      headers.forEach((h: string) => {
        emptyRow[h] = '';
      });

      const worksheet = XLSX.utils.json_to_sheet([emptyRow]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, `${safe}_import_template.xlsx`);
    } catch (e: any) {
      message.error(e?.message || 'Failed to download template');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      const ext = selected.name.split('.').pop()?.toLowerCase();
      if (ext && ['csv', 'xls', 'xlsx'].includes(ext)) {
        setFile(selected);
        setDccSelected(null);
        setDccSelectedMany([]);
        setDccImportStatus({});
      } else {
        message.error('Only CSV or Excel files are allowed');
      }
    }
  };

  const handleSelectFromDcc = async (args: { fullPath: string; name: string }) => {
    try {
      const ext = args.name.split('.').pop()?.toLowerCase();
      if (!ext || !['csv', 'xls', 'xlsx'].includes(ext)) {
        message.error('Only CSV or Excel files are allowed for server-side import from DCC');
        return;
      }

      setDccSelected({ fullPath: args.fullPath, name: args.name });
      setDccSelectedMany([]);
      setFile(null);
      setDccImportStatus({});
      message.success('DCC CSV selected');
    } catch (error: any) {
      message.error(error?.message || 'Failed to select file from DCC');
    }
  };

  const handleSelectManyFromDcc = async (args: Array<{ fullPath: string; name: string }>) => {
    try {
      const selected = (args || []).filter(Boolean);
      if (!selected.length) return;

      const invalid = selected.filter((a) => {
        const ext = a.name.split('.').pop()?.toLowerCase();
        return !ext || !['csv', 'xls', 'xlsx'].includes(ext);
      });
      if (invalid.length) {
        message.error('Only CSV or Excel files are allowed for server-side import from DCC');
        return;
      }

      setDccSelectedMany(selected);
      setDccSelected(null);
      setFile(null);
      setDccImportStatus(() => {
        const next: Record<string, { status: 'pending' | 'running' | 'success' | 'error'; error?: string }> = {};
        selected.forEach((s) => {
          next[s.fullPath] = { status: 'pending' };
        });
        return next;
      });
      message.success(`${selected.length} DCC file(s) selected`);
    } catch (error: any) {
      message.error(error?.message || 'Failed to select files from DCC');
    }
  };

  const handleImport = async () => {
    if (!file && !dccSelected && (!dccSelectedMany || dccSelectedMany.length === 0)) return;
    setShowIfExistsModal(true);
  };

  const handleIfExistsConfirm = async () => {
    setShowIfExistsModal(false);
    setIsUploading(true);
    try {
      const response: any = await (async () => {
        if (dccSelectedMany && dccSelectedMany.length > 0) {
          const results: any[] = [];
          const errors: Array<{ fullPath: string; name: string; error: string }> = [];
          for (let i = 0; i < dccSelectedMany.length; i++) {
            const item = dccSelectedMany[i];
            setDccImportStatus((prev) => ({
              ...(prev || {}),
              [item.fullPath]: { status: 'running' },
            }));
            message.info(`Importing ${i + 1}/${dccSelectedMany.length}: ${item.name}`);
            try {
              const r = await importCsvExcelFromServer(item.fullPath, tableName, ifExists);
              results.push(r);
              setDccImportStatus((prev) => ({
                ...(prev || {}),
                [item.fullPath]: { status: 'success' },
              }));
            } catch (e: any) {
              const errMsg = String(e?.message || e || 'Import failed');
              setDccImportStatus((prev) => ({
                ...(prev || {}),
                [item.fullPath]: { status: 'error', error: errMsg },
              }));
              errors.push({ fullPath: item.fullPath, name: item.name, error: errMsg });
            }
          }
          return { success: errors.length === 0, results, errors };
        }
        if (dccSelected) {
          return importCsvExcelFromServer(dccSelected.fullPath, tableName, ifExists);
        }
        if (!file) throw new Error('No file selected');

        let uploadFile = file;
        // Normalize date formats for specific tables when CSV is provided
        if (tableName === 'mat_gap_deliveries' || tableName === 'mat_gap_deliveries_temporary') {
          const ext = file.name.split('.').pop()?.toLowerCase();
          if (ext === 'csv') {
            const text = await file.text();
            // Replace DD/M/YYYY or D/M/YYYY with YYYY-MM-DD (zero-padded)
            // Only match fields (between commas or line boundaries) to reduce false positives
            const normalized = text.replace(/(^|,)(\s*"?)(\d{1,2})\/(\d{1,2})\/(\d{4})("?\s*)(?=,|\r?\n|$)/g,
              (_m, prefix, preSpaceQuote, d, m, y, postSpaceQuote) => {
                const dd = String(d).padStart(2, '0');
                const mm = String(m).padStart(2, '0');
                return `${prefix}${preSpaceQuote}${y}-${mm}-${dd}${postSpaceQuote}`;
              }
            );
            uploadFile = new File([normalized], file.name, { type: 'text/csv' });
          }
        }

        return importCsvExcel(uploadFile, tableName, ifExists);
      })();
      if (dccSelectedMany && dccSelectedMany.length > 0) {
        const errCount = Array.isArray(response?.errors) ? response.errors.length : 0;
        const okCount = Array.isArray(response?.results) ? response.results.length : 0;
        if (errCount > 0) message.warning(`Import finished: ${okCount} succeeded, ${errCount} failed`);
        else message.success(`Import finished: ${okCount} file(s) succeeded`);
      } else {
        message.success('File imported successfully');
      }
      onSuccess?.(response);
    } catch (error: any) {
      const backendMsg =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        (typeof error === 'string' ? error : null);
      message.error(backendMsg ? `Import failed: ${backendMsg}` : 'Import failed');
      onError?.(error);
    } finally {
      setIsUploading(false);
      setFile(null);
      // Keep DCC selections so the user can see per-file success/fail statuses.
    }
  };

  return (
    <div className="csv-excel-import">
      <Title level={5}>CSV/Excel Import</Title>
      <input
        type="file"
        accept=".csv,.xls,.xlsx"
        onChange={handleFileChange}
        ref={inputRef}
        style={{ display: 'none' }}
        id="csv-import-input"
      />
      <Space wrap>
        <Button 
          icon={<UploadOutlined />} 
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
        >
          Select File
        </Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleDownloadTemplate}
          disabled={isUploading || isDownloadingTemplate}
          loading={isDownloadingTemplate}
        >
          Download Template
        </Button>
        <Button
          onClick={() => setIsDccPickerOpen(true)}
          disabled={isUploading}
        >
          Select from DCC
        </Button>
        <Button
          type="primary"
          onClick={handleImport}
          disabled={(!file && !dccSelected && (!dccSelectedMany || dccSelectedMany.length === 0)) || isUploading}
          loading={isUploading}
        >
          Import to Database
        </Button>
      </Space>
      {dccSelected ? (
        <div style={{ marginTop: 8 }}>
          <span>Selected file (DCC): {dccSelected.name}</span>
        </div>
      ) : null}
      {dccSelectedMany && dccSelectedMany.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <div>Selected files (DCC): {dccSelectedMany.length}</div>
          <div style={{ marginTop: 6, maxHeight: 140, overflow: 'auto' }}>
            {dccSelectedMany.map((it) => (
              <div key={it.fullPath} style={{ lineHeight: '20px' }}>
                <Space size={6}>
                  {dccImportStatus?.[it.fullPath]?.status === 'success' ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  ) : dccImportStatus?.[it.fullPath]?.status === 'error' ? (
                    <Tooltip title={dccImportStatus?.[it.fullPath]?.error} placement="topLeft" overlayStyle={{ maxWidth: 640, wordBreak: 'break-word' as any }}>
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    </Tooltip>
                  ) : dccImportStatus?.[it.fullPath]?.status === 'running' ? (
                    <LoadingOutlined />
                  ) : (
                    <span style={{ width: 14, display: 'inline-block' }} />
                  )}
                  <Tooltip title={it.fullPath} placement="topLeft" overlayStyle={{ maxWidth: 640, wordBreak: 'break-word' as any }}>
                    <span style={{ fontFamily: 'monospace' }}>{it.name}</span>
                  </Tooltip>
                </Space>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {file && !dccSelected ? (
        <div style={{ marginTop: 8 }}>
          <span>Selected file: {file.name}</span>
        </div>
      ) : null}
      <Modal
        open={showIfExistsModal}
        title="Select Import Mode"
        onOk={handleIfExistsConfirm}
        onCancel={() => setShowIfExistsModal(false)}
        okText="Continue"
        cancelText="Cancel"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>Import Mode:</span>
          <Select
            value={ifExists}
            onChange={setIfExists}
            options={[
              { value: 'append', label: 'Append' },
              { value: 'replace', label: 'Replace' }
            ]}
            style={{ width: 220 }}
          />
        </div>
        {ifExists === 'replace' && (
          <div style={{ marginTop: 12 }}>
            <Alert
              type="warning"
              showIcon
              message="Replace mode will delete all existing data before importing"
              description={
                <div>
                  <div>All rows in the target table will be removed, then rows from your file will be inserted.</div>
                  <div style={{ marginTop: 6 }}>The table schema (columns, types, primary keys) will be preserved. If you want to keep existing rows, use <b>Append</b> instead.</div>
                </div>
              }
            />
          </div>
        )}
      </Modal>

      <SelectDCCFileModal
        open={isDccPickerOpen}
        mode="multiple"
        onCancel={() => setIsDccPickerOpen(false)}
        onSelect={(args) => {
          setIsDccPickerOpen(false);
          handleSelectFromDcc(args);
        }}
        onSelectMultiple={(args) => {
          setIsDccPickerOpen(false);
          handleSelectManyFromDcc(args);
        }}
      />
    </div>
  );
};

export default CsvExcelImport;
